import selectAction, {versions} from 'studentapp-ai';
import { getUserData, getStudentMetrics } from './API';
import {
  scopeType,
  interventionType,
  interventionTriggerScopes,
  interventionFrequencyLevels
} from '../constants/interventionSelectionConstants';
import { actionTypes } from '../constants/constants';
import { detectRushing, getDecodingPR } from './readerCharacteristics';
import { cleanAndSplit } from './NLP';
import {logInterventionData, logSessionInfo} from './logger';
import * as Sentry from '@sentry/browser';
import { getDefaultGSFWordBank, logSentryException } from './util';

////
/*
STUDENT APP AI SERVICE
  - Generate input data
  - Utilizes the studentapp-AI npm module to select an action
  - Currently, this module is limited in scope to intervention selection and pause times

INPUT FEATURES
In this context, "input features" is defined an object with three properties, which respectively describe
  - A "story" (a piece of text representing the "right" answers for an activity)
  - A student's interactions with that activity, up until the present (until the current State)
  - Additional data describing a student (e.g. AREA score)
*/

////
// store the phrase corresponding the current set of metadata
let features = {
  story: {
    id: null,
    title: null,
    phrases: [],
    textMetadataForCurrentPhrase: {
      words: [], // an array of word metadata objects for each word in a phrase
      phraseIndex: -1, // the index of the phrase corresponding to the fetched metadata
    },
  },
  interactions:{},
  student: {},
  activityId: null,
  history: null
}

let currentPhrase = 0;
let metadataCache = [];

////
// Keep track of some state for the "readWithMe" mode
let previousWordInterventionsByPhrase = [];
let wordLevelInterventionIndices = [];
let inReadWithMeMode = false;
let readWithMeCount = 0; // counts the number of unique "readWithMe initiation" events (where each read with me is up to four phrases long)
let endingReadWithMeMode = false;
let readWithMeModePhraseCounter = 0; // counts the number of phrases that 'readWithMe' has been enabled for
let prevPhraseErrorCache = null;

let disabledInterventionTypes = [];

// Intervention counts by phrase where the key is the phrase index. This includes giveWord or skipped words from ReaderManager
let interventionCountsByPhrase = {};

export function setDisabledInterventionTypes(types) {
  disabledInterventionTypes = types;
}

// Given a word index, returns a pause time
export function getPauseTime(index) {
  // console.log('SETTING PAUSE TIME AT WORD LEVEL');
  setInteractionAIFeature(actionTypes.WORD_PREP,{
    currentWordIndex: index,
  });
  return runAI();
}

// Return whether app is currently in readWithMe mode
export function getIsInReadWithMeMode() {
  return inReadWithMeMode;
}

export function setCurrentPhrase(phraseIndex){
  currentPhrase = phraseIndex;
  features.story.textMetadataForCurrentPhrase = metadataCache[phraseIndex];
}

// Track which words have recieved pause timeouts (word-level intervention).
// This information is used to trigger ReadWithMe mode
export function trackWordIntervention(index) {
  if(wordLevelInterventionIndices.length > 0 && index < wordLevelInterventionIndices[wordLevelInterventionIndices.length - 1]) {
    console.log('ERROR: a word intervention was triggered on a word with lower index than the previous word intervention. This relates to the tracking in aiService.js, and should never occur.');
  }
  wordLevelInterventionIndices.push(index);
}

// Reset the word-level intervention tracking, and associated readWithMe variables
// Note: this is invoked at the beginning of each story
export function resetWordInterventionTracker() {
  wordLevelInterventionIndices = [];
  previousWordInterventionsByPhrase = [];
  inReadWithMeMode = false;
  readWithMeCount = 0;
  prevPhraseErrorCache = null;
  readWithMeModePhraseCounter = 0;
  interventionCountsByPhrase = {};
}

// Update the word-level intervention tracking at the end of each phrase
export function trackWordInterventionPhraseBoundary() {
  previousWordInterventionsByPhrase.push(wordLevelInterventionIndices);
  wordLevelInterventionIndices = [];
}

// Keep track of the miscues that occurred in the prior phrase, by caching them after checking for readWithMe condition
// TODO - abstract this logic into the studentapp-AI repo
function cachePrevPhraseMiscues() {
  prevPhraseErrorCache = [...features.interactions.errorsInPhrase];
}

// Return true if the readWithMe mode should be triggered
// TODO - abstract this logic into the studentapp-AI repo
function isReadWithMeTriggerCondition() {
  // If features are null
  if(!features || !features.interactions) {
    return false;
  }

  // If user is above 30 WCPM
  if(features.student.WCPM && features.student.WCPM > 30) {
    return false;
  }

  // If not phrase selection, or exceed readWithMe count, return
  if((features.interactions.scope != actionTypes.phrase) || (readWithMeCount >= 3)) {
    return false;
  }
  
  const phraseIndex = features.story.textMetadataForCurrentPhrase.phraseIndex;

  // Also, if this is the last phrase, return
  if (phraseIndex >= features.story.phrases.length - 1) {
    return false;
  }

  // If this isn't the first or second phrase
  if(phraseIndex > 1) {

    // Count num words in the last three phrases
    let currPhraseLength = features.story.phrases[phraseIndex].split(' ').length;
    let minus1PhraseLength = features.story.phrases[phraseIndex - 1].split(' ').length;
    let minus2PhraseLength = features.story.phrases[phraseIndex - 2].split(' ').length;
    let numWords = currPhraseLength + minus1PhraseLength + minus2PhraseLength;

    // Count num interventions in the last three phrases
    let currPhraseInterventions = interventionCountsByPhrase[phraseIndex] ? interventionCountsByPhrase[phraseIndex] : 0;
    let minus1PhraseInterventions = interventionCountsByPhrase[phraseIndex - 1] ? interventionCountsByPhrase[phraseIndex - 1] : 0;
    let minus2PhraseInterventions = interventionCountsByPhrase[phraseIndex - 2] ? interventionCountsByPhrase[phraseIndex - 2] : 0;
    let numInterventions = currPhraseInterventions + minus1PhraseInterventions + minus2PhraseInterventions;

    // re-enable these log statements to test read-with-me behavior:
    // console.log('Testing: num curr phrase interventions', currPhraseInterventions)
    // console.log('Testing: num prev phrase interventions', minus1PhraseInterventions)
    // console.log('Testing: num prev*2 phrase interventions', minus2PhraseInterventions)
    // console.log('Testing: num interventions: ', numInterventions);
    // console.log('Testing: num words: ', numWords);
    // console.log('Testing: num 37', .37*numWords)

    // The code below can be used to look at word timeout counts across the prior two phrases:
    /*
    let currPhraseNumWordInterventions = wordLevelInterventionIndices.length;
    let prevPhraseNumWordInterventions = previousWordInterventionsByPhrase[previousWordInterventionsByPhrase.length - 1].length;
    let numWordInterventions = currPhraseNumWordInterventions + prevPhraseNumWordInterventions;
    */

    if((numInterventions > (.37 * numWords)) && (numWords > 7)) {
      return true;
    }
  }

  return false;
}

// Input params:
// scope - phrase or word level intervention selection
function runAI(forceIntervention = false, interventionBias = undefined) {
  let selection = {};
  let logToSentry = false;

  try {
    // check whether to trigger readWithMe mode
    let triggerReadWithMe = isReadWithMeTriggerCondition();
    if(features.interactions.scope == actionTypes.phrase) {
      cachePrevPhraseMiscues(); // cache this phrase's miscues for use by the readWithMe trigger next phrase
    }
    if(!inReadWithMeMode && triggerReadWithMe) {
      inReadWithMeMode = true;
      readWithMeCount = readWithMeCount + 1;
      selection = {
        decisionContextAcrossPasses: 'within lexa app, triggered readwithme mode',
        type: interventionType.initiateReadWithMe,
        metadata: null,
        word: null,
        history: null,
      }
    } else if (endingReadWithMeMode) {
      inReadWithMeMode = false;
      endingReadWithMeMode = false;
    } else if (inReadWithMeMode && features.interactions.scope == actionTypes.phrase) {
      readWithMeModePhraseCounter += 1;

      let readWithMeCountThreshold = 3; // ReadWithMe mode lasts for four phrases for middle/old readers
      if(features.student.AREA <= 6.5) {
        readWithMeCountThreshold = 1; // ReadWithMe mode lasts for two phrases for young readers
      }
      if (readWithMeModePhraseCounter >= readWithMeCountThreshold) {
        endingReadWithMeMode = true;
        readWithMeModePhraseCounter = 0;
        selection = {
          decisionContextAcrossPasses: 'within lexa app, ending readWithMe mode',
          type: interventionType.endReadWithMe,
          metadata: null,
          word: null,
          history: null,
        }
      } else {
        selection = {
          decisionContextAcrossPasses: 'within lexa app, perpetuated readWithMe mode',
          type: interventionType.readwithme,
          metadata: null,
          word: null,
          history: null,
        }
      }
    }

    // re-enable this log statement to test readWithMe mode behavior:
    /* console.log('read with me phrase counter', readWithMeModePhraseCounter) */

    if(!inReadWithMeMode || features.interactions.scope != actionTypes.phrase) {
      let selectionAndFlag = selectAction(features);
      selection = selectionAndFlag.selection;
      features.history = selection.history;

      // If this is not a none type intervention, add to interventionCountsByPhrase
      if (selection.type !== interventionType.none) {
        const phraseIndex = features.story.textMetadataForCurrentPhrase.phraseIndex;
        addInterventionCountForPhrase(phraseIndex);
      }

      logToSentry = selectionAndFlag.logToSentry;
      if(logToSentry) {
        Sentry.captureMessage("Null Intervention History Bug - replaced student history with empty history to avoid crash");
      }
    }

    if(selection.type != actionTypes.WORD_PREP) {
      let loggingName = "selectIntervention_phrase_" + features.story.textMetadataForCurrentPhrase.phraseIndex + "_word_" + selection.word + "_scope_" + selection.scope + "_type_" + selection.type;
      let selectionContext = {
        features: features,
        selection: selection,
      };
      logInterventionData(features, selection).finally(() => {
        logSessionInfo(loggingName, selectionContext);
      });
      console.log('selected intervention: ',selection);
    }

    selection = performPostProcessingOnType(selection, forceIntervention);
  }catch(err){
    console.log("ERROR IN INTERVENTION SELECTION",err);
    logSessionInfo("selectIntervention_error", {err, features, selection});
    logSentryException(err, "Error in selectIntervention");

    let aType = (features.interactions.scope === actionTypes.WORD_PREP) ? actionTypes.WORD_PREP : interventionType.none;
    selection = {
      type: aType,
      pauseLength: 5250,
    };
    selection = performPostProcessingOnType(selection, forceIntervention);
  }

  return selection;
}

// perform any additional post-processing on the selection type
// necessary for consumption by the app
// NOTE: There are changes on the selection type in readerManager for early reader and if there is a apostrophe
function performPostProcessingOnType(selection, forceIntervention = false) {
  if(selection.type == actionTypes.WORD_PREP) {
    selection = selection.pauseLength;
    return selection;
  }

  // We need to fork the "repeat sentence" intervention
  if(selection.type == 'repeatsentenceErrorsEmphasized') {
    selection.type = interventionType.repeatsentence;
  }

  if(selection.type == interventionType.initiateReadWithMe || selection.type == interventionType.readwithme || selection.type == interventionType.endReadWithMe) {
    selection.word = 'rwmMode'; // placeholder word to get around a null check
    selection.index = 0; // placeholder word to get around a null check
  }

  if(forceIntervention) {
    let isValidForceSelection = validForceIntervention(selection, forceIntervention);
    if(isValidForceSelection) {
      selection.type = interventionType[forceIntervention];

      if (forceIntervention === interventionType.GSF && !selection.gsfWordBank) {
        selection.gsfWordBank = getDefaultGSFWordBank();
      }
    }
    else {
      selection.type = interventionType.none;
    }
  }

  // Add the intervention's "scope" (which determines whether it is played by interventionManager)
  selection.scope = interventionTriggerScopes[selection.type];
  return selection;
}

/////////////////
/////////////////
// Temporary wrappers to expose API calls for intervention selection
// which match the method signature of the older implementation
// TODO deprecate these funcs and simplify the logic surrounding the corresponding calls to these methods
export async function selectWordIntervention(inWord,override, currentWordIndex, forceIntervention = false, interventionBias = undefined) {
  if(override) {
    return override;
  };
  console.log('SELECTING INTERVENTION AT WORD LEVEL');
  setInteractionAIFeature(actionTypes.word,{
    inWord,
    currentWordIndex,
    interventionBias
  });
  return runAI(forceIntervention);
}

export async function selectPhraseIntervention({
  currentErrorCount,
  errorsInPhrase,
  mostRecentScores,
  currentPhraseLength,
  closeErrors,
  skips,
  frameTimes,
  forceIntervention = false,
  interventionBias = undefined,
  phraseMetaData,
  predictionConfidences,
  phraseInterventionWeights
}) {
  const {wcpm: mostRecentWPM, accuracy: mostRecentAccuracy} = mostRecentScores;
  const isRushing = detectRushing(mostRecentWPM, mostRecentAccuracy);
  setInteractionAIFeature(actionTypes.phrase,{
    currentErrorCount,
    errorsInPhrase,
    mostRecentWPM,
    mostRecentAccuracy,
    currentPhraseLength,
    closeErrors,
    isRushing,
    skips,
    frameTimes,
    phraseMetaData,
    predictionConfidences,
    phraseInterventionWeights,
    interventionBias
  });
  console.log('SELECTING INTERVENTION AT PHRASE LEVEL');
  return runAI(forceIntervention);
}

/////////////////
/////////////////
// Setters for the input features
export function resetAIFeaturesForNewStory(story, activityId) {
  features.story = {
    storyId: story.storyId,
    title: story.title,
    phrases: story.phrases,
    textMetadataForCurrentPhrase: {
      phraseIndex: -1, // the index of the phrase corresponding to the fetched metadata
      words: [], // an array of word metadata objects for each word in a phrase
    },
  };

  //Initialize the cache with the words in the story
  metadataCache = story.phrases.map((phrase,phraseIndex)=>{
    let splitPhrase = cleanAndSplit(phrase);
    return {phraseIndex, words: splitPhrase};
  });
  console.log(metadataCache);

  currentPhrase = 0;

  const {
    userId,
    AREA,
    WCPM,
    grade,
    districtId,
    decodeScore,
    isESL = false,
    isEarlyReader = false,
    isSpanishEarlyReader = false,
    locale,
    sorConfig
  } = getUserData();
  const  decodePR = getDecodingPR(decodeScore, grade, locale);
  const interventionFrequency = interventionFrequencyLevels[sorConfig.interventionFrequency] || interventionFrequencyLevels["STANDARD"];

  features.activityId = activityId;
  features.locale = locale;
  features.student = {
    userId,
    AREA,
    grade,
    districtId,
    decodePR,
    decodeScore,
    isESL,
    isEarlyReader,
    isSpanishEarlyReader,
    interventionFrequency,
    WCPM
  };

  console.log(
    `AI features set:
      story: ${story.storyId}
      locale: ${locale}
      SOR intervention frequency: ${sorConfig.interventionFrequency} (${JSON.stringify(interventionFrequency)})
      student area score: ${AREA}
      isESL: ${isESL}
      isEarlyReader: ${isEarlyReader}
  `);
}

// TODO abstract this into the module
export function setPrefetchedMetadataAIFeature(words, phraseIndex) {
  metadataCache[phraseIndex] = {
    phraseIndex,
    words,
  };

  //This is to deal with any race conditions at startup
  if(phraseIndex === currentPhrase){
    features.story.textMetadataForCurrentPhrase = metadataCache[phraseIndex];
  }
}

function setInteractionAIFeature(scope, interactionFeatures) {
  features.interactions = {
    scope,
    ...interactionFeatures,
    disabledInterventionTypes,
  }
}

// TESTING UTILITY
// Function to do a first-order check that the forced intervention is applicable, given word metadata
function validForceIntervention(selection, forceIntervention) {
  const interventionRequiredFields = {
    simpledefinition: ['DEFINITION'],
    clap: ['DEFINITION'],
    funfact: ['FUN_FACT'],
    lipsync: ['VIDEO_URL'],
    rhyme: ['RHYME'],
    sight: ['SIGHT'],
    soundout: ['PHON','GRAPHEMIC_BREAKDOWN','PHON_GRAPHEME_MATCH'],
    sayit: ['GRAPHEMIC_BREAKDOWN'],
    phonemeGiveWord: ['PHON','GRAPHEMIC_BREAKDOWN','PHON_GRAPHEME_MATCH'],
    phonemeLipsync: ['PHONEME_VIDEO_URL'],
    spellout: ['GRAPHEMIC_BREAKDOWN'],
    spelloutBefore: ['GRAPHEMIC_BREAKDOWN'],
    comprehension: [], // TODO
    cognate: ['COGNATE'],
    vocabularyWithPictures: ['DEFINITION','IMG_URL'],
    riddle: ['RIDDLE','RIDDLE_ANSWER'],
    prediction: [],
    morpheme: ['MORPHEME','MORPHEME_POSITION','MORPHEME_MEANING','MORPHEME_ALTS'],
    name1: ['PHON','NAME_FLAG','NAME_NATIONALITY','NAME_MEANING','GRAPHEMIC_BREAKDOWN'],
    name2: ['NAME_PERSON','FUNFACTS_DESCRIPTION'],
    letterFlies: ['PHON','GRAPHEMIC_BREAKDOWN','PHON_GRAPHEME_MATCH'],
    sayASound: ['PHON','GRAPHEMIC_BREAKDOWN','PHON_GRAPHEME_MATCH','PHONEME_VIDEO_URL'],
    soundSeeker: ['PHON'],
    soundout1: ['PHON','GRAPHEMIC_BREAKDOWN','PHON_GRAPHEME_MATCH'],
    soundout2: ['PHON','GRAPHEMIC_BREAKDOWN','PHON_GRAPHEME_MATCH'],
    soundout3: ['PHON','GRAPHEMIC_BREAKDOWN','PHON_GRAPHEME_MATCH'],
    elkoninBox0: ['PHON','GRAPHEMIC_BREAKDOWN'],
    elkoninBox1: ['PHON','GRAPHEMIC_BREAKDOWN'],
    elkoninBox2: ['PHON','GRAPHEMIC_BREAKDOWN'],
    elkoninBox3: ['PHON','GRAPHEMIC_BREAKDOWN'],
    elkoninBox4: ['PHON','GRAPHEMIC_BREAKDOWN'],
    upAndDown1: ['PHON','GRAPHEMIC_BREAKDOWN','PHON_GRAPHEME_MATCH'],
    upAndDown2: ['PHON','GRAPHEMIC_BREAKDOWN','PHON_GRAPHEME_MATCH'],
    upAndDown3: ['PHON','GRAPHEMIC_BREAKDOWN','PHON_GRAPHEME_MATCH'],
    wordDisplay: ['PHON','GRAPHEMIC_BREAKDOWN','DEFINITION','IMG_URL'],
    changeOnePart1: ["PHON", "GRAPHEMIC_BREAKDOWN"],
    giveAndTake1: ["PHON", "GRAPHEMIC_BREAKDOWN"],
    rhyming1: ["RHYME","GRAPHEMIC_BREAKDOWN"],
    rhymingQuiz1: ["RHYME", "NSYL"],
    sayOneSound1: ["PHON", "GRAPHEMIC_BREAKDOWN", "IMG_URL"],
    wordInvestigator1: ["IMG_URL", "PHON"],
    wordInvestigator2: ["IMG_URL"],
    wordScramble1: ["PHON", "GRAPHEMIC_BREAKDOWN"]
  };
  let requiredFields = interventionRequiredFields[forceIntervention];
  let isProbablyValid = true;
  if(requiredFields && selection.metadata) {
    requiredFields.forEach(field => {
      if(!selection.metadata[field] && (selection.metadata[field] !== 0)) {
        isProbablyValid = false;
        console.log('Force intervention aborted due to missing required word metadata: ', field);
      }
    });
  }
  return isProbablyValid;
}

// Increments the intervention count for a given phrase index
export function addInterventionCountForPhrase(phraseIndex) {
  if (interventionCountsByPhrase[phraseIndex]) {
    interventionCountsByPhrase[phraseIndex] = interventionCountsByPhrase[phraseIndex] + 1;
  } else {
    interventionCountsByPhrase[phraseIndex] = 1;
  }
}


// End of feature setters
/////////////////
/////////////////

export default {
  selectPhraseIntervention,
  selectWordIntervention,
  setPrefetchedMetadataAIFeature,
  resetAIFeaturesForNewStory,
  addInterventionCountForPhrase,
}